import React from "react";
import { ImageIntersectionObserverContext } from "fr-ito-web-react/react/esm";

interface ImageIntersectionObserverProviderProps {
  children: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
}

export const ImageIntersectionObserverProvider: React.FC<
  ImageIntersectionObserverProviderProps
> = ({ children, rootMargin = "100px 0px 300px 0px", threshold = 0.1 }) => {
  const observer = React.useRef<IntersectionObserver | null>(null);

  const observe = React.useCallback(
    (ref: React.RefObject<HTMLImageElement>) => {
      if (!observer.current) {
        observer.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const img = entry.target as HTMLImageElement;
                const dataSrc = img.dataset.src;
                if (dataSrc && img.src !== dataSrc) {
                  img.src = dataSrc;
                }
                observer.current?.unobserve(img);
              }
            });
          },
          { threshold, rootMargin }
        );
      }
      if (ref.current) {
        observer.current.observe(ref.current);
      }
    },
    [rootMargin, threshold]
  );

  const unobserve = React.useCallback(
    (ref: React.RefObject<HTMLImageElement>) => {
      if (ref.current && observer.current) {
        observer.current.unobserve(ref.current);
      }
    },
    []
  );

  React.useEffect(
    () => () => {
      observer.current?.disconnect();
    },
    []
  );

  const contextValue = React.useMemo(
    () => ({ observe, unobserve }),
    [observe, unobserve]
  );

  return (
    <ImageIntersectionObserverContext.Provider value={contextValue}>
      {children}
    </ImageIntersectionObserverContext.Provider>
  );
};

export default ImageIntersectionObserverProvider;
